import {
    useCallback,
    useMemo,
    useRef,
    useState,
    createContext,
    type ReactNode,
} from "react";
import toast from "react-hot-toast";

import { useComputeRouteMutation } from "../../../api";
import { formatDistance, formatDuration } from "../../../lib/units";
import useDeviceLocation from "../../../hooks/useDeviceLocation";

type MapContextType = {
    coords: google.maps.LatLng | google.maps.LatLngLiteral;
    handleMarkerDragEnd: (event: google.maps.MapMouseEvent) => void;
    calculateRoute: (
        startLoc?: {
            lat: number;
            lng: number;
        },
        endLoc?: {
            lat: number;
            lng: number;
        }
    ) => void;
    distance: string | undefined;
    duration: string | undefined;
    /**
     * Where the browser says the person is, or null.
     *
     * Null until the permission is granted, and null forever if it is refused
     * or the device cannot say. Every map falls back to FALLBACK_CENTRE in that
     * case, so a refusal costs a less useful first view and nothing else.
     */
    userLocation: { lat: number; lng: number } | null;
    /**
     * Radius of the reported position's uncertainty, in metres, or 0.
     *
     * Drawn as the soft disc under the location dot. A fix good to two
     * kilometres and one good to five metres are the same dot without it, and
     * the difference is whether the dot is where the person is.
     */
    userAccuracyMetres: number;
    /** True while the browser is being asked, for the recentre control. */
    isLocating: boolean;
    /**
     * Asks the browser again, at full accuracy. Attached to the recentre
     * control: this is the one that may raise the permission prompt.
     */
    locateUser: () => void;
    /** The road route between the two ends, ready to draw. Empty until one
     *  has been computed. */
    routePath: google.maps.LatLngLiteral[];
    /**
     * True when the API had no map provider configured and the geometry it
     * returned is the straight line between the points. A client drawing a
     * line across a lagoon should know that is what it is doing.
     */
    routeIsDirect: boolean;
    startLocation: {
        lat: number;
        lng: number;
    };
    setStartLocation: React.Dispatch<
        React.SetStateAction<{
            lat: number;
            lng: number;
        }>
    >;
    endLocation: {
        lat: number;
        lng: number;
    };
    setEndLocation: React.Dispatch<
        React.SetStateAction<{
            lat: number;
            lng: number;
        }>
    >;
};

// The context object is exported alongside the provider on purpose; this file
// is the module that owns both, and splitting them buys nothing but an import.
// eslint-disable-next-line react-refresh/only-export-components
export const MapContext = createContext<MapContextType>(
    [] as unknown as MapContextType
);

type Props = {
    children: ReactNode;
};

/**
 * Where a map opens before anything better is known.
 *
 * Chicago, which is where this application was first built. It is a poor
 * default for a service pricing rides in naira, which is why the provider asks
 * the browser where the person actually is and only falls back to this.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const FALLBACK_CENTRE = { lat: 41.88183, lng: -87.623177 };

export const MapProvider = ({ children }: Props) => {
    /**
     * Where the browser says the person is.
     *
     * Asked once on mount at low accuracy, which is enough to centre a map and
     * bias an address search and costs a noticeably faster first fix and less
     * battery than GPS. A refusal is silent: there is no state for "denied"
     * because nothing behaves differently, the maps simply keep the fallback
     * they would have used. The recentre control asks again, at full accuracy,
     * because that answer is one someone pressed a button for.
     */
    const device = useDeviceLocation({ watchOnMount: true });
    const { fix, isLocating, locate } = device;

    // Memoised on the fix rather than rebuilt per render: this object is a
    // dependency of every map that centres on it, and a fresh literal each
    // render re-runs their fitBounds effects on every keystroke elsewhere.
    const userLocation = useMemo(
        () => (fix ? { lat: fix.latitude, lng: fix.longitude } : null),
        [fix]
    );

    const locateUser = useCallback(() => {
        void locate();
    }, [locate]);

    const [coords, setCoords] = useState<
        google.maps.LatLng | google.maps.LatLngLiteral
    >({ lat: 41.88183, lng: -87.623177 });
    const routeErrorReported = useRef(false);
    const [computeRoute] = useComputeRouteMutation();
    const [routePath, setRoutePath] = useState<google.maps.LatLngLiteral[]>([]);
    const [routeIsDirect, setRouteIsDirect] = useState(false);
    const [distance, setDistance] = useState<string>();
    const [duration, setDuration] = useState<string>();

    const [startLocation, setStartLocation] = useState({
        lat: 0,
        lng: 0,
    });

    const [endLocation, setEndLocation] = useState({
        lat: 0,
        lng: 0,
    });

    const handleMarkerDragEnd = (event: google.maps.MapMouseEvent) => {
        const lat = event.latLng?.lat();
        const lng = event.latLng?.lng();
        if (lat && lng) {
            setCoords({ lat, lng });
        }
    };

    /**
     * Asks the API for the road route between the two ends.
     *
     * Server-side, not `google.maps.DirectionsService`. The browser key is
     * restricted to the APIs the map itself needs, and Directions was not among
     * them, so every request came back DIRECTIONS_ROUTE: REQUEST_DENIED and the
     * route line and journey time simply never appeared. Every caller invokes
     * this as `void calculateRoute()`, so the rejection became an unhandled
     * promise and nothing else: no line, no distance, no time, and no clue why.
     *
     * The API already computes this for the mobile clients, holds the only key
     * that needs routing rights, and returns the geometry as coordinates as
     * well as an encoded polyline, so nothing here has to decode anything.
     *
     * Reported once per session. A misconfigured key denies every request, and
     * a toast per address pair would bury the screen.
     */
    const calculateRoute = useCallback(
        async function calculateRoute(
            startLoc?: { lat: number; lng: number },
            endLoc?: { lat: number; lng: number }
        ) {
            const from = startLoc ?? startLocation;
            const to = endLoc ?? endLocation;
            if (!from.lat || !to.lat) return;

            try {
                // Longitude first, matching the API and PostGIS.
                const route = await computeRoute({
                    origin: { longitude: from.lng, latitude: from.lat },
                    destination: { longitude: to.lng, latitude: to.lat },
                }).unwrap();

                setRoutePath(
                    route.path.map((point) => ({
                        lat: point.latitude,
                        lng: point.longitude,
                    }))
                );
                setRouteIsDirect(route.source === "direct");
                setDistance(formatDistance(route.distanceMeters));
                setDuration(formatDuration(route.durationSeconds));
            } catch (error) {
                console.error("Route request failed", error);
                if (!routeErrorReported.current) {
                    routeErrorReported.current = true;
                    toast.error(
                        "We could not work out the route for that journey."
                    );
                }
            }
        },
        [startLocation, endLocation, computeRoute]
    );

    // Memoised: an inline object literal is a new value on every render, so
    // every consumer of this context re-rendered whenever the provider did,
    // which on this page is on every mouse drag of the map marker.
    const value = useMemo(
        () => ({
            distance,
            duration,
            userLocation,
            userAccuracyMetres: fix?.accuracyMetres ?? 0,
            isLocating,
            locateUser,
            routePath,
            routeIsDirect,
            startLocation,
            setStartLocation,
            endLocation,
            setEndLocation,
            coords,
            calculateRoute,
            handleMarkerDragEnd,
        }),
        [
            distance,
            duration,
            userLocation,
            fix,
            isLocating,
            locateUser,
            routePath,
            routeIsDirect,
            startLocation,
            endLocation,
            coords,
            calculateRoute,
        ]
    );

    return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
};
